import React, { useMemo, useState } from 'react';
import { Plus, Search, WifiOff } from 'lucide-react';
import Card, { CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import { AdminEmptyState, AdminNotice, AdminSkeletonRows } from '../../components/admin/AdminState';
import { useRole } from '../../context/RoleContext';
import { useAdminData } from '../../context/AdminDataContext';
import { toDeviceViewModel } from '../../lib/api/adapters';
import { BackendDeviceStatus, ProvisionDeviceResponse } from '../../lib/api/contracts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table';

import { measureProductAction, trackProductInteraction } from '../../lib/productAnalytics';

type DeviceFilter = 'all' | BackendDeviceStatus;

const MUTATION_DISABLED_TEXT = 'Requires authenticated admin token / endpoint support.';
const statusToBadgeVariant = (status: BackendDeviceStatus): 'success' | 'warning' | 'error' | 'outline' => {
  if (status === 'ACTIVE') {
    return 'success';
  }
  if (status === 'PROVISIONED') {
    return 'warning';
  }
  if (status === 'SUSPENDED' || status === 'REVOKED') {
    return 'error';
  }
  return 'outline';
};

const DeviceProvisioning: React.FC = () => {
  const { role, activeCompanyId, setActiveCompanyId } = useRole();
  const { clients, devices, loading, error, provisionDevice, revokeDevice, rotateDeviceKey } = useAdminData();
  const [searchTerm, setSearchTerm] = useState('');
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilter>('all');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState('1');
  const [publicKey, setPublicKey] = useState('');
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [capabilities, setCapabilities] = useState('FACIAL_RECOGNITION');
  const [provisionedDevice, setProvisionedDevice] = useState<ProvisionDeviceResponse | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState<'provision' | 'revoke' | 'rotate' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const activeCompany = clients.find((company) => company.id === activeCompanyId) ?? clients[0] ?? null;

  const companyDevices = devices.filter((device) => device.client_id === activeCompany?.id);
  const filteredDevices = useMemo(
    () =>
      companyDevices
        .filter((device) => (deviceFilter === 'all' ? true : device.status === deviceFilter))
        .filter((device) =>
          [
            device.id,
            device.device_id,
            clients.find((client) => client.id === device.client_id)?.name ?? '',
            device.status,
          ]
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
        .map(toDeviceViewModel),
    [clients, companyDevices, deviceFilter, searchTerm]
  );
  const selectedDevice = filteredDevices.find((device) => device.id === selectedDeviceId) ?? filteredDevices[0] ?? null;

  React.useEffect(() => {
    if (!activeCompanyId && clients[0]?.id) {
      setActiveCompanyId(clients[0].id);
    }
  }, [activeCompanyId, clients, setActiveCompanyId]);

  const handleRevoke = async () => {
    if (!selectedDevice) {
      setActionError('Select a device first.');
      return;
    }

    setActionSubmitting('revoke');
    setActionError(null);
    setActionSuccess(null);

    try {
      await measureProductAction('device_revoke', () => revokeDevice(selectedDevice.id));
      setActionSuccess(`${selectedDevice.deviceId} was revoked.`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to revoke device.');
    } finally {
      setActionSubmitting(null);
    }
  };

  const handleProvision = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!activeCompany) {
      setActionError('Select a client first.');
      return;
    }

    const capabilityList = capabilities
      .split(',')
      .map((capability) => capability.trim())
      .filter(Boolean);

    if (capabilityList.length === 0) {
      setActionError('At least one device capability is required.');
      return;
    }

    setActionSubmitting('provision');
    setActionError(null);
    setActionSuccess(null);
    setProvisionedDevice(null);

    try {
      const response = await measureProductAction('device_provision', () => provisionDevice({
        client_id: activeCompany.id,
        ...(employeeId.trim() ? { employee_id: employeeId.trim() } : {}),
        capabilities: capabilityList,
      }));
      setProvisionedDevice(response);
      setEmployeeId('');
      setActionSuccess('Device provisioned. Save the one-time credentials below now.');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to provision device.');
    } finally {
      setActionSubmitting(null);
    }
  };

  const handleRotate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedDevice) {
      setActionError('Select a device first.');
      return;
    }

    const parsedVersion = Number(newVersion);
    if (!Number.isInteger(parsedVersion) || parsedVersion <= 0) {
      setActionError('New key version must be a positive integer.');
      return;
    }

    if (!publicKey.trim()) {
      setActionError('Base64 public key is required.');
      return;
    }

    setActionSubmitting('rotate');
    setActionError(null);
    setActionSuccess(null);

    try {
      await measureProductAction('device_key_rotate', () => rotateDeviceKey({
        device_id: selectedDevice.id,
        client_id: selectedDevice.clientId,
        new_version: parsedVersion,
        public_key: publicKey.trim(),
      }));
      setPublicKey('');
      setActionSuccess(`${selectedDevice.deviceId} key was rotated.`);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to rotate device key.');
    } finally {
      setActionSubmitting(null);
    }
  };

  if (role !== 'super_admin') {
    return (
      <Card className="border-[var(--admin-amber)] bg-[var(--admin-amber-soft)]">
        <CardHeader>
          <CardTitle>Super User Access Required</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-[var(--admin-muted)]">
          Device operations remain restricted to super users.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <section className="admin-page-header">
        <div>
          <h1 className="text-3xl font-semibold">Devices</h1>
          <p className="mt-1 text-sm text-[var(--admin-muted)]">
            Backend-backed device inventory for the selected client.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={activeCompany?.id ?? ''}
            onChange={(event) => { trackProductInteraction('company_switch'); setActiveCompanyId(event.target.value); }}
            className="rounded-full border border-[var(--admin-border)] bg-white/80 px-4 py-2.5 text-sm"
          >
            {clients.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => { if (!isProvisioning) trackProductInteraction('device_form_opened'); setIsProvisioning((current) => !current); }}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--admin-border)] bg-[var(--admin-text)] px-4 py-2.5 text-sm font-semibold text-white"
          >
            <Plus size={16} />
            Provision Device
          </button>
        </div>
      </section>

      <AdminNotice tone="success">
        Provision, revoke, and key rotation use the current authenticated admin API contracts.
      </AdminNotice>

      {isProvisioning ? (
        <Card className="border-[var(--admin-cyan)] bg-[var(--admin-surface-strong)]">
          <CardHeader>
            <CardTitle>Provision Device</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProvision} className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="employeeId" className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--admin-muted)]">
                  Employee ID (optional)
                </label>
                <input
                  id="employeeId"
                  value={employeeId}
                  onChange={(event) => setEmployeeId(event.target.value)}
                  placeholder="Employee UUID"
                  className="mt-1 w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  disabled={actionSubmitting !== null}
                />
              </div>
              <div>
                <label htmlFor="capabilities" className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--admin-muted)]">
                  Capabilities
                </label>
                <input
                  id="capabilities"
                  value={capabilities}
                  onChange={(event) => setCapabilities(event.target.value)}
                  placeholder="FACIAL_RECOGNITION, GPS"
                  className="mt-1 w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                  disabled={actionSubmitting !== null}
                />
              </div>
              <button
                type="submit"
                disabled={actionSubmitting !== null || !activeCompany}
                className="rounded-xl bg-[var(--admin-text)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 md:col-span-2"
              >
                {actionSubmitting === 'provision' ? 'Provisioning...' : `Provision for ${activeCompany?.name ?? 'client'}`}
              </button>
            </form>

            {provisionedDevice ? (
              <div className="mt-4 space-y-2 rounded-2xl border border-[var(--admin-amber)] bg-[var(--admin-amber-soft)] p-4 text-sm">
                <p className="font-semibold">One-time provisioning credentials</p>
                <p><span className="font-medium">Device record:</span> {provisionedDevice.device_id}</p>
                <p><span className="font-medium">Activation code:</span> {provisionedDevice.activation_code ?? 'Not returned'}</p>
                <p className="break-all"><span className="font-medium">Private key:</span> {provisionedDevice.private_key ?? 'Not returned'}</p>
                <p className="text-xs text-[var(--admin-muted)]">Store these securely; the backend only returns them during provisioning.</p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {error ? <AdminNotice tone="error" title="Failed to load devices">{error}</AdminNotice> : null}

      <section className="admin-page-kpis">
        <Card className="bg-[var(--admin-surface-strong)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Fleet Size</CardTitle>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">{companyDevices.length}</CardContent>
        </Card>
        <Card className="bg-[var(--admin-surface-strong)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Active</CardTitle>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">
            {companyDevices.filter((device) => device.status === 'ACTIVE').length}
          </CardContent>
        </Card>
        <Card className="bg-[var(--admin-surface-strong)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Provisioned</CardTitle>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">
            {companyDevices.filter((device) => device.status === 'PROVISIONED').length}
          </CardContent>
        </Card>
        <Card className="bg-[var(--admin-surface-strong)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-[0.16em] text-[var(--admin-muted)]">Suspended/Revoked</CardTitle>
          </CardHeader>
          <CardContent className="text-3xl font-semibold">
            {companyDevices.filter((device) => device.status === 'SUSPENDED' || device.status === 'REVOKED').length}
          </CardContent>
        </Card>
      </section>

      <section className="admin-filter-bar rounded-[1.6rem] border border-[var(--admin-border)] bg-[#f7f1e6]/88 px-4 py-3">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative max-w-md flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--admin-muted)]" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search device ID or status"
              className="w-full rounded-full border border-[var(--admin-border)] bg-white/80 py-2.5 pl-9 pr-4 text-sm"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {(['all', 'PROVISIONED', 'ACTIVE', 'SUSPENDED', 'REVOKED'] as DeviceFilter[]).map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => { trackProductInteraction('device_filter', { filter }); setDeviceFilter(filter); }}
                className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] ${
                  deviceFilter === filter
                    ? 'bg-[var(--admin-text)] text-white'
                    : 'border border-[var(--admin-border)] bg-white/70 text-[var(--admin-muted)]'
                }`}
              >
                {filter === 'all' ? 'All' : filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(20rem,0.75fr)]">
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Device Inventory</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? <AdminSkeletonRows /> : null}
            {!loading && filteredDevices.length === 0 ? (
              <AdminEmptyState title="No devices found">
                Adjust filters or select another client. The frontend only renders backend device rows.
              </AdminEmptyState>
            ) : null}
            {!loading && filteredDevices.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device ID</TableHead>
                    <TableHead>Record ID</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Revoked</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDevices.map((device) => (
                    <TableRow
                      key={device.id}
                      data-state={device.id === selectedDevice?.id ? 'selected' : undefined}
                      className="cursor-pointer"
                      onClick={() => {
                        setSelectedDeviceId(device.id);
                        setActionError(null);
                        setActionSuccess(null);
                      }}
                    >
                      <TableCell className="font-medium">{device.deviceId}</TableCell>
                      <TableCell className="text-xs text-[var(--admin-muted)]">{device.id}</TableCell>
                      <TableCell>{clients.find((client) => client.id === device.clientId)?.name ?? 'Unknown'}</TableCell>
                      <TableCell>
                        <Badge variant={statusToBadgeVariant(device.status)}>{device.statusLabel}</Badge>
                      </TableCell>
                      <TableCell>{device.createdAt}</TableCell>
                      <TableCell>{device.revokedAt ?? 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Backend Field Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-[var(--admin-muted)]">
              <div className="rounded-2xl border border-[var(--admin-border)] bg-white/75 px-4 py-3">
                This API currently exposes `device_id`, `client_id`, status, created/revoked timestamps.
              </div>
              <div className="rounded-2xl border border-[var(--admin-border)] bg-white/75 px-4 py-3">
                Device type, serial number, site, admin owner, and attendance counters are not available yet.
              </div>
              <div className="rounded-2xl border border-[var(--admin-border)] bg-[#171513] px-4 py-3 text-stone-300">
                Revoke and rotate use the backend device record ID. Provisioning is intentionally blocked until a matching
                endpoint exists.
              </div>
            </CardContent>
          </Card>

          <Card className="border-[var(--admin-rose)] bg-[var(--admin-rose-soft)]">
            <CardHeader className="pb-2">
              <CardTitle className="text-[var(--admin-rose)]">Needs Attention</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-2 text-sm text-[var(--admin-rose)]">
              <WifiOff size={14} />
              {companyDevices.filter((device) => device.status === 'SUSPENDED' || device.status === 'REVOKED').length} device(s) in suspended/revoked state.
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Write Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-2xl border border-[var(--admin-border)] bg-white/75 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.14em] text-[var(--admin-muted)]">Selected Device</p>
                <p className="mt-1 font-semibold">{selectedDevice?.deviceId ?? 'None selected'}</p>
                <p className="mt-1 text-xs text-[var(--admin-muted)]">{selectedDevice?.id ?? 'Select a row to act.'}</p>
              </div>

              <button
                type="button"
                disabled={!selectedDevice || selectedDevice.status === 'REVOKED' || actionSubmitting !== null}
                onClick={handleRevoke}
                className="w-full rounded-xl border border-[var(--admin-rose)] bg-[var(--admin-rose-soft)] px-4 py-2 text-sm font-semibold text-[var(--admin-rose)] disabled:cursor-not-allowed disabled:opacity-60"
                title={MUTATION_DISABLED_TEXT}
              >
                {actionSubmitting === 'revoke' ? 'Revoking...' : 'Revoke Device'}
              </button>

              <form onSubmit={handleRotate} className="space-y-3 rounded-2xl border border-[var(--admin-border)] bg-white/75 p-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--admin-muted)]" htmlFor="newVersion">
                    New Version
                  </label>
                  <input
                    id="newVersion"
                    value={newVersion}
                    onChange={(event) => setNewVersion(event.target.value)}
                    inputMode="numeric"
                    className="mt-1 w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                    disabled={actionSubmitting !== null}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--admin-muted)]" htmlFor="publicKey">
                    Base64 Public Key
                  </label>
                  <textarea
                    id="publicKey"
                    value={publicKey}
                    onChange={(event) => setPublicKey(event.target.value)}
                    rows={4}
                    className="mt-1 w-full rounded-xl border border-[var(--admin-border)] px-3 py-2 text-sm"
                    disabled={actionSubmitting !== null}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!selectedDevice || actionSubmitting !== null}
                  className="w-full rounded-xl bg-[var(--admin-text)] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {actionSubmitting === 'rotate' ? 'Rotating...' : 'Rotate Device Key'}
                </button>
              </form>

              {actionError ? <AdminNotice tone="error">{actionError}</AdminNotice> : null}

              {actionSuccess ? <AdminNotice tone="success">{actionSuccess}</AdminNotice> : null}
            </CardContent>
          </Card>
        </div>
      </section>

      {loading ? <p className="text-sm text-[var(--admin-muted)]">Refreshing device inventory...</p> : null}
    </div>
  );
};

export default DeviceProvisioning;
